import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination } from '@mui/material';

function SalesActionStatTable({ filteredEvents = [], onRowClick }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  React.useEffect(() => {
    setPage(0);
  }, [filteredEvents]);

  const pagedEvents = React.useMemo(() => {
    return filteredEvents.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredEvents, page, rowsPerPage]);

  // 공통 테이블 셀 스타일 매크로 (가독성 및 간격 조절용)
  const cellStyle = {
    color: '#374151',
    py: 0.75, 
    px: 1,
    fontSize: '13px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  return (
    <Paper 
      elevation={0}
      sx={{ 
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)', 
        borderRadius: '12px',
        border: '1px solid rgba(0, 0, 0, 0.03)',
        overflow: 'hidden',
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        mt: -0.5
      }}
    >
      <TableContainer sx={{ height: 290, overflowY: 'auto' }}>
        <Table stickyHeader sx={{ minWidth: 800, tableLayout: 'fixed' }} size="small" aria-label="sales action stat detailed table">
          <TableHead>
            <TableRow>
              {/* 🚀 테이블 컬럼 너비 비율 합계 100% 최적화 배치 */}
              <TableCell align="left" sx={{ fontWeight: '600', color: '#4B5563', py: 1, pl: 3, backgroundColor: '#F9FAFB', width: '12%' }}>업무구분</TableCell>
              <TableCell align="center" sx={{ fontWeight: '600', color: '#4B5563', py: 1, backgroundColor: '#F9FAFB', width: '14%' }}>일시</TableCell>
              <TableCell align="center" sx={{ fontWeight: '600', color: '#4B5563', py: 1, backgroundColor: '#F9FAFB', width: '14%' }}>업체</TableCell>
              <TableCell align="center" sx={{ fontWeight: '600', color: '#4B5563', py: 1, backgroundColor: '#F9FAFB', width: '9%' }}>업체 유형</TableCell>
              <TableCell align="center" sx={{ fontWeight: '600', color: '#4B5563', py: 1, backgroundColor: '#F9FAFB', width: '8%' }}>장소</TableCell>
              <TableCell align="center" sx={{ fontWeight: '600', color: '#4B5563', py: 1, backgroundColor: '#F9FAFB', width: '9%' }}>제품</TableCell>
              <TableCell align="left" sx={{ fontWeight: '600', color: '#4B5563', py: 1, backgroundColor: '#F9FAFB', width: '26%' }}>제목</TableCell>
              <TableCell align="center" sx={{ fontWeight: '600', color: '#4B5563', py: 1, pr: 2, backgroundColor: '#F9FAFB', width: '8%' }}>담당자명</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedEvents.length > 0 ? (
              pagedEvents.map((row, index) => (
                <TableRow 
                  key={row.actionId || row.actionCode || index}
                  hover
                  onClick={() => onRowClick && onRowClick(row)}
                  sx={{ 
                    height: 36, 
                    cursor: onRowClick ? 'pointer' : 'default',
                    '&:last-child td, &:last-child th': { border: 0 }, 
                    '&:hover': { backgroundColor: '#F9FAFB' } 
                  }}
                >
                  <TableCell align="left" sx={{ ...cellStyle, pl: 3, fontWeight: '500', wordBreak: 'break-all' }}>
                    {row.actionName || '-'}
                  </TableCell>
                  <TableCell align="center" sx={cellStyle}>
                    {row.startDatetime && row.endDatetime ? `${row.startDatetime.substring(0, 16)} ~ ${row.endDatetime.substring(11, 16)}` : row.totalTime || '-'}
                  </TableCell>
                  <TableCell align="center" sx={cellStyle}>
                    {row.companyNm || '-'}
                  </TableCell>
                  <TableCell align="center" sx={cellStyle}>
                    {row.partnerTyp || '-'}
                  </TableCell>
                  <TableCell align="center" sx={cellStyle}>
                    {row.meetPlace || '-'}
                  </TableCell>
                  {/* 🚀 제품 컬럼 데이터 필드 바인딩 정상화 */}
                  <TableCell align="center" sx={{ ...cellStyle, fontWeight: '500' }}>
                    {row.productNm || '-'}
                  </TableCell>
                  {/* 🚀 제목 컬럼: 텍스트가 기므로 정렬을 왼쪽(left)으로 맞추고 말줄임 적용 */}
                  <TableCell align="left" sx={{ ...cellStyle, fontWeight: '500', whiteSpace: 'nowrap' }}>
                    {row.title || '-'}
                  </TableCell>
                  <TableCell align="center" sx={{ ...cellStyle, pr: 2 }}>
                    {row.usrNm || '-'}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow style={{ height: 240 }}>
                {/* 🚀 헤더 컬럼 수(8개)와 일치하도록 colSpan 수정 */}
                <TableCell colSpan={8} align="center" sx={{ color: 'text.secondary', fontSize: '13px' }}>
                  조회된 통계 데이터가 없습니다.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={filteredEvents.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="페이지당 행 수:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} / 전체 ${count}개`}
        sx={{ borderTop: '1px solid #f3f4f6', backgroundColor: '#ffffff', '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': { fontSize: '12px' } }}
      />
    </Paper>
  );
}

export default SalesActionStatTable;
